import React, { useEffect, useMemo, useRef } from "react";
import { deviceTagsQuery } from "@/components/custom/devices/device-tags";
import { useQuery } from "@tanstack/react-query";
import {
  client,
  MinimalEntityDto,
  ListSamplesDto,
  UxSourceDto,
  $TransactionResultEnum,
  UserDetailsDto,
  CatalogItemDetailsDto,
  SiteGroupSummaryDto,
} from "@/client";
import { FormField } from "@/components/ui/form";
import { useFormContext, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useTimeSettings } from "@/state/time-settings";
import {
  FormSelectInputItem,
  FormSelectInputItemCommonProps,
} from "@/components/custom/form-inputs/select-input/select-input";
import { useOptionsMapping } from "@/components/custom/form-inputs/select-input/common";
import {
  FormDateRangePickerItem,
  FormDateRangePickerItemCommonProps,
} from "@/components/custom/form-inputs/date-range-picker";
import {
  FormInputItem,
  FormInputItemCommonProps,
} from "@/components/custom/form-inputs/debounced-input";
import { cn } from "@/lib/utils";
import { useTags } from "@/hooks/use-tags";
import { useFilterIsActive } from "@/components/custom/data-table/data-table-context";
import { useDataTableFiltersSubmit } from "@/components/custom/data-table/buttons/data-table-filters";
import {
  FormSelectInputCreatableItem,
  FormSelectInputCreatableItemCommonProps,
} from "@/components/custom/form-inputs/select-input/select-input-creatable";
import * as uuid from "uuid";
import {
  FormSelectInputAsyncPaginateQueryItem,
  FormSelectInputAsyncPaginateQueryItemCommonProps,
} from "@/components/custom/form-inputs/select-input/select-input-async-pagination";
import {
  FormSelectInputAsyncPaginateQueryCreatableItem,
  FormSelectInputAsyncPaginateQueryCreatableItemCommonProps,
} from "@/components/custom/form-inputs/select-input/select-input-async-pagination-creatable";
import {
  FormCheckbox,
  FormCheckboxCommonProps,
} from "@/components/custom/form-inputs/checkbox";
import { usePermissions } from "@/hooks/use-permissions";
import { components } from "react-select";
import { Button } from "@/components/custom/button";
import { applyTranslation } from "@/components/custom/images-table/translations";
import { cascadeRegionFilter } from "./-images-region-filter";
import { withPermission } from "@/components/custom/with-permission";
import { listPermissions } from "@/lib/list-permissions";

type TForm = ListSamplesDto;

export function TagsMatchRadioFilter() {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();
  return (
    <FormField
      control={form.control}
      name="tagsMatch"
      render={({ field }) => {
        return (
          <div className="flex ml-auto items-center gap-2 text-radio pr-9">
            <div
              className="flex items-center gap-1"
              onClick={() => field.onChange("none")}
            >
              <input
                {...field}
                className="scale-90"
                type="radio"
                id="tagsMatchNone"
                name="tagsMatchNone"
                value={"none"}
                checked={field.value === "none"}
              />
              <label htmlFor="tagsMatchNone">{t("none")}</label>
            </div>
            <div
              className="flex items-center gap-1"
              onClick={() => field.onChange("any")}
            >
              <input
                {...field}
                className="scale-90"
                type="radio"
                id="tagsMatchAny"
                name="tagsMatchAny"
                value={"any"}
                checked={field.value === undefined || field.value === "any"}
              />
              <label htmlFor="tagsMatchAny">{t("containsAny")}</label>
            </div>
            <div
              className="flex items-center gap-1"
              onClick={() => field.onChange("all")}
            >
              <input
                {...field}
                className="scale-90"
                type="radio"
                id="tagsMatchAll"
                name="tagsMatchAll"
                value={"all"}
                checked={field.value === "all"}
              />
              <label htmlFor="tagsMatchAll">{t("containsAll")}</label>
            </div>
            <div
              className="flex items-center gap-1"
              onClick={() => field.onChange("exact")}
            >
              <input
                {...field}
                className="scale-90"
                type="radio"
                id="tagsMatchExact"
                name="tagsMatchExact"
                value={"exact"}
                checked={field.value === "exact"}
              />
              <label htmlFor="tagsMatchExact">{t("exactMatch")}</label>
            </div>
          </div>
        );
      }}
    />
  );
}

export function TagsMatchSelectFilter(props: FormSelectInputItemCommonProps) {
  const { t } = useTranslation();
  const options = React.useMemo(
    () => [
      { value: "any", label: t("matchAny"), additional: [undefined] },
      { value: "none", label: t("matchNone") },
      { value: "all", label: t("matchAll") },
      { value: "exact", label: t("exactMatch") },
    ],
    [],
  );
  const form = useFormContext<TForm>();
  const mapping = useOptionsMapping(options);
  return (
    <FormSelectInputItem
      form={form}
      name="tagsMatch"
      label={t("tagsMatch")}
      mapping={mapping as any}
      options={options}
      isMulti={false}
      {...props}
    />
  );
}

function SampleTagsQuickButtons({
  form,
  ...props
}: Parameters<typeof components.MenuList>[0] & {
  form: UseFormReturn<TForm, any, undefined>;
}) {
  const values = Array.isArray(props.selectProps.value)
    ? props.selectProps.value
    : [props.selectProps.value];
  const sampleTagsMatch = form.watch("tagsMatch");
  return (
    <div className="pb-1">
      <Button
        variant={sampleTagsMatch === "none" ? "azureOutline" : "default"}
        className="w-full justify-normal"
        // className={cn(sampleTagsMatch === "none" && "text-azure")}
        onClick={() => {
          if (sampleTagsMatch === "none") {
            form.setValue("tagsMatch", undefined);
          } else {
            form.setValue("tagsMatch", "none");
          }
          props.selectProps.onChange([], {
            action: "clear",
            removedValues: values,
          });
        }}
      >
        {sampleTagsMatch === "none" && values.length > 0 ? "None of" : "None"}
      </Button>
    </div>
  );
}

const TagsFilter = withPermission(
  listPermissions.sampleTags,
  TagsFilterControl,
);
function TagsFilterControl({
  withQuickButtons,
  onChange,
  ...props
}: FormSelectInputItemCommonProps & {
  // submit?: () => Promise<any>;
  withQuickButtons?: boolean;
}) {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();
  const { tagOptions: options } = useTags({
    tagsListQuery: client.tags.listSummaries
      .assign({
        page: 1,
        limit: 9999,
      })
      .asQueryOptions({}) as any,
  });
  const mapping = useOptionsMapping(options);
  return (
    <FormSelectInputItem
      form={form}
      name="tagIds"
      label={t("tags")}
      mapping={mapping as any}
      options={options}
      isMulti={true}
      delimiter={"\n"}
      container={{
        className: cn("md:col-span-2"),
        right: TagsMatchRadioFilter,
      }}
      isActive={useFilterIsActive(["tagIds", "tagsMatch"])}
      components={{
        MenuList: React.useCallback<any>(
          (props: Parameters<typeof components.MenuList>[0]) => {
            return (
              <components.MenuList {...props}>
                {withQuickButtons && (
                  <SampleTagsQuickButtons {...props} form={form} />
                )}
                {props.children}
              </components.MenuList>
            );
          },
          [],
        ),
      }}
      onChange={(v, a) => {
        if (withQuickButtons) {
          if (Array.isArray(v) && v?.length > 0) {
            form.setValue("tagsMatch", undefined);
          }
        }
        if (onChange) {
          onChange(v, a);
        }
      }}
      {...props}
    />
  );
}

const ModelsFilter = withPermission(
  listPermissions.models,
  ModelsFilterControl,
);
function ModelsFilterControl(props: FormSelectInputItemCommonProps) {
  const { t } = useTranslation();
  const form = useFormContext<TForm>();
  const perPage = 10;
  return (
    <FormSelectInputAsyncPaginateQueryItem
      form={form}
      name="modelIds"
      label={t("models")}
      mapping={(v) => ({ value: v!, label: v as unknown as string })}
      queryKey={client.models.listMinimal.queryKey} // cache
      queryCreator={client.models.listMinimal.asQueryOptions<any>}
      createOptions={(data) =>
        data.results.map((row: MinimalEntityDto) => ({
          label: [row.name, row.id ? ` (${row.id})` : undefined]
            .filter(Boolean)
            .join(" "),
          value: row.id!,
        }))
      }
      hasMore={(data, variable) =>
        data.results.length >= perPage && data.total > variable.page * perPage
      }
      requestVariable={(search, page, limit) => ({
        search,
        limit: limit ?? perPage,
        page,
      })}
      isMulti={false}
      container={{ className: cn("grow") }}
      {...props}
    />
  );
}

function ShowNonScansFilter() {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();
  return (
    <FormCheckbox
      form={form}
      name="showNonScans"
      label={t("showNonScans")}
      container={{ className: "pr-8 ml-4" }}
    />
  );
}

function HeadBlurredFilter(props: FormSelectInputItemCommonProps) {
  const { t } = useTranslation();
  const form = useFormContext<TForm>();
  const options = React.useMemo(
    () => [
      { value: undefined as any, label: t("all") },
      { value: true as any, label: t("headBlurred") },
      { value: false as any, label: t("notHeadBlurred") },
    ],
    [t],
  );
  const mapping = useOptionsMapping(options) as (
    value: (typeof options)[number]["value"] | undefined,
  ) => (typeof options)[number];
  return (
    <FormSelectInputItem
      form={form}
      name="headBlurred"
      label={t("headBlur")}
      options={options}
      mapping={mapping}
      isActive={useFilterIsActive("headBlurred")}
      {...props}
    />
  );
}

function HeadsFoundFilter(props: FormSelectInputItemCommonProps) {
  const { t } = useTranslation();
  const form = useFormContext<TForm>();
  const options = React.useMemo(
    () => [
      { value: undefined as any, label: t("all") },
      { value: true as any, label: t("headsFound") },
      { value: false as any, label: t("headsNotFound") },
    ],
    [t],
  );
  const mapping = useOptionsMapping(options) as (
    value: (typeof options)[number]["value"] | undefined,
  ) => (typeof options)[number];
  return (
    <FormSelectInputItem
      form={form}
      name="headsFound"
      label={t("headsFound")}
      options={options}
      mapping={mapping}
      isActive={useFilterIsActive("headsFound")}
      {...props}
    />
  );
}

function StatusFilter(props: FormSelectInputItemCommonProps) {
  const { t } = useTranslation();

  const options = React.useMemo(
    () =>
      $TransactionResultEnum.enum.map((v) => ({
        value: v as any,
        label: t(v),
      })),
    [t],
  );

  const form = useFormContext<TForm>();
  const mapping = useOptionsMapping(options);
  return (
    <FormSelectInputItem
      form={form}
      name="result"
      label={t("status")}
      mapping={mapping}
      options={options}
      isMulti={true}
      isActive={useFilterIsActive("result")}
      {...props}
    />
  );
}

const FamiliesFilter = withPermission(
  listPermissions.catalogFamilies,
  FamiliesFilterControl,
);
function FamiliesFilterControl(
  props: FormSelectInputAsyncPaginateQueryItemCommonProps,
) {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();
  const perPage = 10;
  return (
    <FormSelectInputAsyncPaginateQueryItem
      form={form}
      name="families"
      label={t("family")}
      isMulti={true}
      mapping={(v) => ({ value: v!, label: v as unknown as string })}
      queryCreator={client.catalog.getFamilies.asQueryOptions as unknown as any}
      createOptions={(data: any) => {
        if (!data?.results || !Array.isArray(data.results)) return [];
        return data.results.map((item: string) => ({
          label: item,
          value: item,
        }));
      }}
      hasMore={(data: any, variable: any) =>
        data?.results?.length >= perPage &&
        data?.total > variable.page * perPage
      }
      requestVariable={(search, page, limit) => ({
        name: search,
        limit: limit ?? perPage,
        page,
      })}
      isActive={useFilterIsActive("families")}
      {...props}
    />
  );
}

function MatchStrengthFilter(props: FormSelectInputItemCommonProps) {
  const { t } = useTranslation();

  const options = [
    { value: undefined as any, label: "All" },
    { label: "Match", value: "match" },
    { label: "Alert non-match", value: "alert-non-match" },
    // { label: 'Suspect non-match', value: 'suspect-non-match' },
    { label: "Not guarded", value: "not-guarded" },
    // { label: 'Unknown', value: 'unknown' },
  ];

  const form = useFormContext<TForm>();
  const mapping = useOptionsMapping(options);
  return (
    <FormSelectInputItem
      form={form}
      name="matchStrength"
      label={t("matchStrength")}
      mapping={mapping}
      options={options}
      isMulti={false}
      {...props}
    />
  );
}

function SampleFilter(props: FormSelectInputCreatableItemCommonProps) {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();
  return (
    <FormSelectInputCreatableItem
      form={form}
      name="sampleId"
      label={t("sampleIds")}
      mapping={(v) => ({ value: v as any, label: v as any })}
      inputValidator={(v) => uuid.validate(v)}
      isMulti={true}
      // container={{ className: cn("md:col-span-2") }}
      {...props}
    />
  );
}

function SessionFilter(props: FormSelectInputCreatableItemCommonProps) {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();
  return (
    <FormSelectInputCreatableItem
      form={form}
      name="sessionId"
      label={t("sessionIds")}
      mapping={(v) => ({ value: v as any, label: v as any })}
      inputValidator={(v) => uuid.validate(v)}
      isMulti={true}
      // container={{ className: cn("md:col-span-2") }}
      {...props}
    />
  );
}

const PredictionsFilter = withPermission(
  listPermissions.catalogItems,
  PredictionsFilterControl,
);
function PredictionsFilterControl(props: FormSelectInputItemCommonProps) {
  const { t } = useTranslation();
  const form = useFormContext<TForm>();
  const perPage = 10;
  return (
    <FormSelectInputAsyncPaginateQueryCreatableItem
      form={form}
      name="predictions"
      label={t("predictions")}
      isMulti={true}
      // container={{ className: cn("md:col-span-2") }}
      mapping={(v) => ({ value: v!, label: v as unknown as string })}
      queryKey={client.catalog.listSummaries.queryKey}
      queryCreator={client.catalog.listSummaries.asQueryOptions<any>}
      createOptions={(data) =>
        data.results.map((row: CatalogItemDetailsDto) => ({
          label: applyTranslation(row.plu, row.englishName || row.name)!,
          value: row.plu!,
          name: row.name,
          englishName: row.englishName,
        }))
      }
      hasMore={(data, variable) =>
        data.results.length >= perPage && data.total > variable.page * perPage
      }
      requestVariable={(search, page, limit, additional) => ({
        search: search,
        limit: limit ?? perPage,
        page,
        exact: additional?.exact,
      })}
      isActive={useFilterIsActive("predictions")}
      optionSerializer={"value"}
      searchOnPaste={true}
      {...props}
    />
  );
}

const LabelsFilter = withPermission(
  listPermissions.catalogItems,
  LabelsFilterControl,
);
function LabelsFilterControl(
  props: FormSelectInputAsyncPaginateQueryCreatableItemCommonProps,
) {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();
  const perPage = 10;
  return (
    <FormSelectInputAsyncPaginateQueryCreatableItem
      form={form}
      name="labels"
      label={t("labels")}
      isMulti={true}
      // container={{ className: cn("md:col-span-2") }}
      mapping={(v) => ({ value: v!, label: v as unknown as string })}
      queryKey={client.catalog.listSummaries.queryKey}
      queryCreator={client.catalog.listSummaries.asQueryOptions<any>}
      createOptions={(data) =>
        data.results.map((row: CatalogItemDetailsDto) => ({
          label: applyTranslation(row.plu, row.englishName || row.name)!,
          value: row.plu!,
          name: row.name,
          englishName: row.englishName,
        }))
      }
      hasMore={(data, variable) =>
        data.results.length >= perPage && data.total > variable.page * perPage
      }
      requestVariable={(search, page, limit, additional) => ({
        search: search,
        limit: limit ?? perPage,
        page,
        exact: additional?.exact,
      })}
      isActive={useFilterIsActive("labels")}
      optionSerializer={"value"}
      searchOnPaste={true}
      {...props}
    />
  );
}

function DatesFilter(
  props: FormDateRangePickerItemCommonProps & FormInputItemCommonProps,
) {
  const { t } = useTranslation();
  const form = useFormContext<TForm>();
  const timeSettings = useTimeSettings();
  return (
    <FormDateRangePickerItem
      form={form}
      from="fromDate"
      to="toDate"
      label={t("dates")}
      {...timeSettings}
      container={{ className: cn("grow") }}
      isActive={useFilterIsActive(["fromDate", "toDate"])}
      {...(props as any)}
    />
  );
}

function DatesAndTypeFilter(
  props: FormDateRangePickerItemCommonProps & FormInputItemCommonProps,
) {
  // const { t } = useTranslation();
  // const form = useFormContext<TForm>();
  return (
    // <span className={"md:col-span-2"}>
    //   <div className="flex gap-2">
    <DatesFilter {...props} />
    //     <FormCheckbox
    //       form={form}
    //       name="timeField"
    //       label={t("agentTime")}
    //       container={{ className: "pr-8 ml-4" }}
    //     />
    //   </div>
    // </span>
  );
}

const SiteGroupsFilter = withPermission(
  listPermissions.siteGroups,
  SiteGroupsFilterControl,
);
function SiteGroupsFilterControl(
  props: FormSelectInputAsyncPaginateQueryItemCommonProps,
) {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();
  const perPage = 10;
  return (
    <FormSelectInputAsyncPaginateQueryItem
      form={form}
      name="siteGroupIds"
      label={t("siteGroups")}
      isMulti={true}
      mapping={(v) => ({ value: v!, label: v as unknown as string })}
      queryKey={client.siteGroups.listSummaries.queryKey}
      queryCreator={client.siteGroups.listSummaries.asQueryOptions<any>}
      createOptions={(data) =>
        (data.results ?? []).map((row: SiteGroupSummaryDto) => ({
          label: row.name,
          value: row.id,
        }))
      }
      hasMore={(data: any, variable: any) =>
        (data?.results?.length ?? 0) >= perPage &&
        (data?.total ?? 0) > variable.page * perPage
      }
      requestVariable={(search, page, limit) => ({
        name: search,
        limit: limit ?? perPage,
        page,
      })}
      isActive={useFilterIsActive("siteGroupIds")}
      {...props}
    />
  );
}

const SitesFilter = withPermission(listPermissions.sites, SitesFilterControl);
function SitesFilterControl(props: FormSelectInputItemCommonProps) {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();
  const perPage = 10;
  return (
    <FormSelectInputAsyncPaginateQueryItem
      form={form}
      name="siteIds"
      label={t("sites")}
      isMulti={true}
      // container={{ className: cn("md:col-span-2") }}
      mapping={(v) => ({ value: v!, label: v as unknown as string })}
      queryKey={client.sites.listMinimal.queryKey} // cache
      queryCreator={client.sites.listMinimal.asQueryOptions<any>}
      createOptions={(data) => [
        { label: t("unassigned"), value: -1 },
        ...data.results.map((row: MinimalEntityDto) => ({
          label: row.name!,
          value: row.id!,
        })),
      ]}
      hasMore={(data, variable) =>
        data.results.length >= perPage && data.total > variable.page * perPage
      }
      requestVariable={(search, page, limit) => ({
        name: search,
        limit: limit ?? perPage,
        page,
      })}
      delimiter={"\n"}
      isActive={useFilterIsActive("siteIds")}
      {...props}
    />
  );
}

const ClustersFilter = withPermission(
  listPermissions.clusters,
  ClustersFilterControl,
);
function ClustersFilterControl(props: FormSelectInputItemCommonProps) {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();
  const perPage = 10;
  return (
    <FormSelectInputAsyncPaginateQueryItem
      form={form}
      name="clusterIds"
      label={t("clusters")}
      isMulti={true}
      // container={{ className: cn("md:col-span-2") }}
      mapping={(v) => ({ value: v!, label: v as unknown as string })}
      queryKey={client.clusters.listMinimal.queryKey} // cache
      queryCreator={client.clusters.listMinimal.asQueryOptions<any>}
      createOptions={(data) => [
        { label: t("unassigned"), value: -1 },
        ...data.results.map((row: MinimalEntityDto) => ({
          label: row.name!,
          value: row.id!,
        })),
      ]}
      hasMore={(data, variable) =>
        data.results.length >= perPage && data.total > variable.page * perPage
      }
      requestVariable={(search, page, limit) => ({
        name: search,
        limit: limit ?? perPage,
        page,
      })}
      // isActive={useFilterIsActive("clusterIds")}
      {...props}
    />
  );
}

const StorageDevicesFilter = withPermission(
  listPermissions.devices,
  StorageDevicesFilterControl,
);
function StorageDevicesFilterControl(
  props: FormSelectInputAsyncPaginateQueryItemCommonProps,
) {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();
  const perPage = 10;
  return (
    <FormSelectInputAsyncPaginateQueryItem
      form={form}
      name="storageDeviceIds"
      label={t("storageDevices")}
      isMulti={true}
      // container={{ className: cn("md:col-span-2") }}
      mapping={(v) => ({ value: v!, label: v as unknown as string })}
      queryKey={client.devices.listMinimal.queryKey} // cache
      queryCreator={client.devices.listMinimal.asQueryOptions<any>}
      createOptions={(data) =>
        data.results.map((row: MinimalEntityDto) => ({
          label: row.name!,
          value: row.id!,
        }))
      }
      hasMore={(data, variable) =>
        data.results.length >= perPage && data.total > variable.page * perPage
      }
      requestVariable={(search, page, limit) => ({
        name: search,
        limit: limit ?? perPage,
        page,
      })}
      {...props}
    />
  );
}

const DevicesFilter = withPermission(
  listPermissions.devices,
  DevicesFilterControl,
);
function DevicesFilterControl(
  props: FormSelectInputAsyncPaginateQueryItemCommonProps,
) {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();
  const perPage = 10;
  return (
    <FormSelectInputAsyncPaginateQueryItem
      form={form}
      name="deviceIds"
      label={t("devices")}
      isMulti={true}
      // container={{ className: cn("md:col-span-2") }}
      mapping={(v) => ({ value: v!, label: v as unknown as string })}
      queryKey={client.devices.listMinimal.queryKey} // cache
      queryCreator={client.devices.listMinimal.asQueryOptions<any>}
      createOptions={(data) =>
        data.results.map((row: MinimalEntityDto) => ({
          label: row.name!,
          value: row.id!,
        }))
      }
      hasMore={(data, variable) =>
        data.results.length >= perPage && data.total > variable.page * perPage
      }
      requestVariable={(search, page, limit) => ({
        name: search,
        limit: limit ?? perPage,
        page,
      })}
      isActive={useFilterIsActive("deviceIds")}
      {...props}
    />
  );
}

function DeviceIpFilter(props: FormInputItemCommonProps) {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();

  return (
    <FormInputItem
      form={form}
      name="deviceIp"
      label={t("ip")}
      type="string"
      isUndefinable={true}
      {...props}
    />
  );
}

// ID-6524: region and sub-region by ID from the regions tables, cascading -- the sub-region list is
// scoped to the chosen region and a region change drops a sub-region picked under the previous one
// (cascadeRegionFilter, unit-tested). Replaces a free-text field that matched the legacy site text.
// Left out for a role that cannot call GET /sites/regions, as ID-7084 does for every list-backed
// control: its 403 would eject the user to /dashboard/unauthorized.
const RegionFilter = withPermission(
  listPermissions.regions,
  RegionFilterControl,
);
function RegionFilterControl(
  props: FormSelectInputAsyncPaginateQueryItemCommonProps,
) {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();
  const perPage = 10;
  const regionId = form.watch("regionId");
  // Read in render so the effect sees the live value (react-hook-form's formState is a proxy). The
  // filter form is created empty and reset from the URL AFTER mount; that reset is not a user
  // change and must not cascade, or a deep-linked sub-region is wiped the moment the popover opens
  // and auto-applied on close. A reset leaves the form clean; a user's pick makes it dirty.
  const isDirty = form.formState.isDirty;
  const previousRegionId = useRef(regionId);
  const labelById = useRef(new Map<number, string>());
  useEffect(() => {
    if (previousRegionId.current === regionId) return;
    if (!isDirty) {
      previousRegionId.current = regionId;
      return;
    }
    const next = cascadeRegionFilter(
      {
        regionId: previousRegionId.current,
        subRegionId: form.getValues("subRegionId"),
      },
      regionId,
    );
    previousRegionId.current = regionId;
    if (next.subRegionId !== form.getValues("subRegionId")) {
      // `null`, so the Sub Region select actually shows the clear -- see cascadeRegionFilter. The
      // form's field type comes from the request DTO, which never carries null: the URL layer
      // (coerceRegionSearch) turns it back into "absent" before anything is sent.
      form.setValue(
        "subRegionId",
        next.subRegionId as unknown as TForm["subRegionId"],
        { shouldDirty: true },
      );
    }
  }, [regionId, form, isDirty]);

  return (
    <FormSelectInputAsyncPaginateQueryItem
      form={form}
      name="regionId"
      label={t("region")}
      isMulti={false}
      isClearable={true}
      // A clear writes null: the Controller re-renders on null, not on undefined (see
      // cascadeRegionFilter); coerceRegionSearch keeps it out of the URL.
      empty={null}
      // The selected value is an id; the label comes from the options seen so far, else the id.
      mapping={(v) => ({
        value: v!,
        label: labelById.current.get(v!) ?? String(v),
      })}
      queryKey={client.sites.getRegions.queryKey} // cache
      queryCreator={client.sites.getRegions.asQueryOptions<any>}
      createOptions={(data) =>
        data.results.map((row: MinimalEntityDto) => {
          labelById.current.set(row.id!, row.name!);
          return { label: row.name!, value: row.id! };
        })
      }
      hasMore={(data, variable) =>
        data.results.length >= perPage && data.total > variable.page * perPage
      }
      requestVariable={(search, page, limit) => ({
        name: search,
        limit: limit ?? perPage,
        page,
      })}
      isActive={useFilterIsActive("regionId")}
      {...props}
    />
  );
}

const SubRegionFilter = withPermission(
  listPermissions.regions,
  SubRegionFilterControl,
);
function SubRegionFilterControl(
  props: FormSelectInputAsyncPaginateQueryItemCommonProps,
) {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();
  const perPage = 10;
  const regionId = form.watch("regionId");
  const labelById = useRef(new Map<number, string>());

  return (
    <FormSelectInputAsyncPaginateQueryItem
      form={form}
      name="subRegionId"
      label={t("subRegion")}
      isMulti={false}
      isClearable={true}
      empty={null}
      // The option cache is per region: a switch drops the previous region's options WITHOUT
      // remounting the select -- a remount re-captures the field's current value as the
      // Controller's fallback, which is how the old sub-region stayed on screen.
      cacheUniqs={[regionId]}
      isDisabled={!regionId}
      placeholder={regionId ? undefined : t("selectARegion")}
      mapping={(v) => ({
        value: v!,
        label: labelById.current.get(v!) ?? String(v),
      })}
      queryKey={client.sites.getSubRegions.queryKey} // cache
      queryCreator={client.sites.getSubRegions.asQueryOptions<any>}
      createOptions={(data) =>
        data.results.map((row: MinimalEntityDto) => {
          labelById.current.set(row.id!, row.name!);
          return { label: row.name!, value: row.id! };
        })
      }
      hasMore={(data, variable) =>
        data.results.length >= perPage && data.total > variable.page * perPage
      }
      // No region, no options: the loader treats null as "nothing to offer".
      requestVariable={(search, page, limit) =>
        regionId
          ? { name: search, regionId, limit: limit ?? perPage, page }
          : null
      }
      isActive={useFilterIsActive("subRegionId")}
      {...props}
    />
  );
}

const LabelSourceFilter = withPermission(
  listPermissions.labelSources,
  LabelSourceFilterControl,
);
function LabelSourceFilterControl(props: FormSelectInputItemCommonProps) {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();
  const perPage = 50;

  return (
    <FormSelectInputAsyncPaginateQueryItem
      form={form}
      name="uxSource"
      label={t("labelSource")}
      isMulti={false}
      mapping={(v) => ({ value: v!, label: v as unknown as string })}
      queryKey={client.samples.getUxSource.queryKey}
      queryCreator={client.samples.getUxSource.asQueryOptions<any>}
      createOptions={(data) =>
        (data?.results ?? []).map((row: UxSourceDto) => ({
          label: row.uxSource,
          value: row.uxSource,
        }))
      }
      hasMore={(data, variable) =>
        (data?.results?.length ?? 0) >= perPage &&
        (data?.total ?? 0) > variable.page * perPage
      }
      requestVariable={(_search, page, limit) => ({
        page,
        limit: limit ?? perPage,
      })}
      isActive={useFilterIsActive("uxSource")}
      {...props}
    />
  );
}

function OnlyImagesFilter(props: FormCheckboxCommonProps) {
  const { t } = useTranslation();

  const form = useFormContext<TForm>();
  return (
    <FormCheckbox
      form={form}
      name="showOnlyWithImages"
      label={t("showOnlyWithImages")}
      isActive={useFilterIsActive("showOnlyWithImages")}
      {...props}
    />
  );
}

function DeviceTagsMatchRadioFilter() {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();
  return (
    <FormField
      control={form.control}
      name="devicesTagsMatch"
      render={({ field }) => {
        return (
          <div className="flex ml-auto items-center gap-2 text-radio pr-9">
            <div
              className="flex items-center gap-1"
              onClick={() => field.onChange("none")}
            >
              <input
                {...field}
                type="radio"
                id="deviceTagsMatchNone"
                name="deviceTagsMatchNone"
                value={"none"}
                checked={field.value === "none"}
              />
              <label htmlFor="deviceTagsMatchNone">{t("none")}</label>
            </div>
            <div
              className="flex items-center gap-1"
              onClick={() => field.onChange("any")}
            >
              <input
                {...field}
                type="radio"
                id="deviceTagsMatchAny"
                name="deviceTagsMatchAny"
                value={"any"}
                checked={field.value === undefined || field.value === "any"}
              />
              <label htmlFor="deviceTagsMatchAny">{t("containsAny")}</label>
            </div>
            <div
              className="flex items-center gap-1"
              onClick={() => field.onChange("all")}
            >
              <input
                {...field}
                type="radio"
                id="deviceTagsMatchAll"
                name="deviceTagsMatchAll"
                value={"all"}
                checked={field.value === "all"}
              />
              <label htmlFor="deviceTagsMatchAll">{t("containsAll")}</label>
            </div>
            <div
              className="flex items-center gap-1"
              onClick={() => field.onChange("exact")}
            >
              <input
                {...field}
                type="radio"
                id="deviceTagsMatchExact"
                name="deviceTagsMatchExact"
                value={"exact"}
                checked={field.value === "exact"}
              />
              <label htmlFor="deviceTagsMatchExact">{t("exactMatch")}</label>
            </div>
          </div>
        );
      }}
    />
  );
}

const DeviceTagsFilter = withPermission(
  listPermissions.deviceTags,
  DeviceTagsFilterControl,
);
function DeviceTagsFilterControl(props: FormSelectInputItemCommonProps) {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();
  const { tagOptions: options } = useTags({
    tagsListQuery: deviceTagsQuery,
  });
  const mapping = useOptionsMapping(options);
  return (
    <FormSelectInputItem
      form={form}
      name="devicesTagIds"
      label={t("deviceTags")}
      mapping={mapping as any}
      options={options}
      isMulti={true}
      delimiter={"\n"}
      container={{
        className: cn("md:col-span-2"),
        right: DeviceTagsMatchRadioFilter,
      }}
      isActive={useFilterIsActive("devicesTagIds")}
      {...props}
    />
  );
}

const TaggedByFilter = withPermission(
  listPermissions.userDetails,
  TaggedByFilterControl,
);
function TaggedByFilterControl(props: FormSelectInputItemCommonProps) {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();

  const { data } = useQuery(
    client.users.getAllUsers.asQueryOptions({ page: 1, limit: 9999 }),
  );
  const options = useMemo(() => {
    const arr = [{ value: undefined as any, label: "All" }];
    if (!data?.results) return arr;

    data?.results.forEach((user: UserDetailsDto) => {
      arr.push({
        value: user.id,
        label: `${user.firstName} ${user.lastName}`.trim(),
      });
    });
    return arr;
  }, [data]);
  const mapping = useOptionsMapping(options);
  return (
    <FormSelectInputItem
      form={form}
      name="taggedByIds"
      label={t("taggedBy")}
      mapping={mapping}
      options={options}
      isMulti={true}
      isActive={useFilterIsActive("taggedByIds")}
      {...props}
    />
  );
}

const DatasetFilter = withPermission(
  listPermissions.datasets,
  DatasetFilterControl,
);
function DatasetFilterControl(
  props: FormSelectInputItemCommonProps & { disabled?: boolean },
) {
  const form = useFormContext<TForm>();
  const { t } = useTranslation();
  const { disabled, ...restProps } = props;

  const { data } = useQuery(client.staticDatasets.listMinimal.asQueryOptions());
  const options = useMemo(() => {
    const arr = [
      { value: -1, label: t("production", { defaultValue: "Production" }) },
    ];
    if (!data) return arr;

    data.forEach((dataset) => {
      arr.push({
        value: dataset.id,
        label: dataset.name,
      });
    });
    return arr;
  }, [data, t]);
  const mapping = useOptionsMapping(options) as (
    value: (typeof options)[number]["value"] | undefined,
  ) => (typeof options)[number];
  return (
    <FormSelectInputItem
      form={form}
      name="datasetId"
      label={t("dataset", { defaultValue: "Dataset" })}
      mapping={mapping}
      options={options}
      isMulti={false}
      isActive={useFilterIsActive("datasetId")}
      isDisabled={disabled}
      {...restProps}
    />
  );
}

export function ImagesFilters({
  disableDatasetFilter,
}: { disableDatasetFilter?: boolean } = {}) {
  const { permissions } = usePermissions();
  return (
    <>
      <DatesAndTypeFilter />
      {permissions.datasets.view && (
        <DatasetFilter disabled={disableDatasetFilter} />
      )}
      <DevicesFilter />
      <DeviceIpFilter />
      <RegionFilter />
      <SubRegionFilter />
      <LabelSourceFilter />
      <StorageDevicesFilter />
      <ClustersFilter />
      <SitesFilter />
      <SiteGroupsFilter />
      <OnlyImagesFilter />
      <LabelsFilter />
      <PredictionsFilter />
      <MatchStrengthFilter />
      <SampleFilter />
      <SessionFilter />
      <FamiliesFilter />
      <StatusFilter />
      <HeadBlurredFilter />
      <HeadsFoundFilter />
      <div className="md:col-span-2 flex">
        <ModelsFilter />
        <ShowNonScansFilter />
      </div>{" "}
      <TagsFilter />
      <DeviceTagsFilter />
      {permissions.samples.viewTaggedBy && <TaggedByFilter />}
    </>
  );
}

export function LabelDistributionImagesFilters() {
  const { permissions } = usePermissions();
  return (
    <>
      <DatesAndTypeFilter />
      <OnlyImagesFilter />
      <PredictionsFilter />
      <MatchStrengthFilter />
      <SampleFilter />
      <StatusFilter />
      <div className="md:col-span-2 flex">
        <ModelsFilter />
        <ShowNonScansFilter />
      </div>
      <TagsFilter />
      <DeviceTagsFilter />
      {permissions.samples.viewTaggedBy && <TaggedByFilter />}
    </>
  );
}

export function ImagesFiltersInline({
  disableDatasetFilter,
}: { disableDatasetFilter?: boolean } = {}) {
  const submit = useDataTableFiltersSubmit();
  const { permissions } = usePermissions();
  return (
    <>
      <DatesFilter
        inline={true}
        container={{
          clear: null,
          className: "space-y-0",
          sideBySide: false,
          size: "sm",
        }}
        button={{ className: "h-8" }}
        input={{ className: "h-6" }}
        onClose={submit}
        clearable={true}
      />
      {permissions.datasets.view && (
        <DatasetFilter
          disabled={disableDatasetFilter}
          inline={true}
          container={{
            clear: null,
            className: "space-y-0",
            sideBySide: false,
            size: "sm",
          }}
          classNames={{ control: (_s) => "py-0 h-8 !min-h-8 !w-auto" }}
          onChange={submit}
        />
      )}
      <StatusFilter
        inline={true}
        container={{
          clear: null,
          className: "space-y-0",
          sideBySide: false,
          size: "sm",
        }}
        hideSelectedOptions={false}
        onChange={submit}
        classNames={{ control: (_s) => "py-0 h-8 !min-h-8 !w-auto" }}
      />
      <SitesFilter
        inline={true}
        container={{
          clear: null,
          className: "space-y-0",
          sideBySide: false,
          size: "sm",
        }}
        hideSelectedOptions={false}
        onChange={submit}
        classNames={{ control: (_s) => "py-0 h-8 !min-h-8 !w-auto" }}
      />
      <DevicesFilter
        inline={true}
        container={{
          clear: null,
          className: "space-y-0",
          sideBySide: false,
          size: "sm",
        }}
        hideSelectedOptions={false}
        onChange={submit}
        classNames={{ control: (_s) => "py-0 h-8 !min-h-8 !w-auto" }}
      />
      <TagsFilter
        inline={true}
        container={{
          clear: null,
          className: "space-y-0",
          sideBySide: false,
          size: "sm",
        }}
        hideSelectedOptions={false}
        onChange={submit}
        classNames={{ control: (_s) => "py-0 h-8 !min-h-8 !w-auto" }}
        withQuickButtons={true}
      />
      <LabelsFilter
        inline={true}
        container={{
          clear: null,
          className: "space-y-0",
          sideBySide: false,
          size: "sm",
        }}
        hideSelectedOptions={false}
        onChange={submit}
        classNames={{ control: (_s) => "py-0 h-8 !min-h-8 !w-auto" }}
      />
      <FamiliesFilter
        inline={true}
        container={{
          clear: null,
          className: "space-y-0",
          sideBySide: false,
          size: "sm",
        }}
        hideSelectedOptions={false}
        onChange={submit}
        classNames={{ control: (_s) => "py-0 h-8 !min-h-8 !w-auto" }}
      />
    </>
  );
}

export function LabelDistributionImagesFiltersInline() {
  const submit = useDataTableFiltersSubmit();
  return (
    <>
      <DatesFilter
        inline={true}
        container={{
          clear: null,
          className: "space-y-0",
          sideBySide: false,
          size: "sm",
        }}
        button={{ className: "h-8" }}
        input={{ className: "h-6" }}
        onClose={submit}
        clearable={true}
      />
      <StatusFilter
        inline={true}
        container={{
          clear: null,
          className: "space-y-0",
          sideBySide: false,
          size: "sm",
        }}
        hideSelectedOptions={false}
        onChange={submit}
        classNames={{ control: (_s) => "py-0 h-8 !min-h-8 !w-auto" }}
      />
      <TagsFilter
        inline={true}
        container={{
          clear: null,
          className: "space-y-0",
          sideBySide: false,
          size: "sm",
        }}
        hideSelectedOptions={false}
        onChange={submit}
        classNames={{ control: (_s) => "py-0 h-8 !min-h-8 !w-auto" }}
        withQuickButtons={true}
      />
    </>
  );
}
